import React, { useCallback, useMemo, useState } from 'react';
import {
  Chart,
  PointElement,
  LineElement,
  TimeScale,
  Colors,
  Tooltip,
  LinearScale,
  Title,
  Filler,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import 'chartjs-adapter-moment';
import moment from 'moment-timezone';
import PerformanceUtils from 'services/PerformanceUtils';

const vLinePlugin = {
  id: 'vline',
  enable: true,
  afterDraw: (chart, args, options) => {
    if (options.enabled && options.index !== null) {
      const {
        ctx,
        chartArea: { top, bottom },
        scales: { x },
      } = chart;
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(x.getPixelForValue(options.index), top);
      ctx.lineTo(x.getPixelForValue(options.index), bottom);
      ctx.lineWidth = 2;
      ctx.strokeStyle = '#42bcdd';
      ctx.stroke();
      ctx.restore();
    }
  },
};

Chart.register(
  vLinePlugin,
  PointElement,
  LineElement,
  TimeScale,
  Colors,
  LinearScale,
  Title,
  Filler,
  Tooltip,
  Legend,
  Filler,
);

const formatter = new Intl.NumberFormat('en-CA', {
  style: 'currency',
  currency: 'CAD',
});

const DEFAULT_ARRAY = [];

export const FancyAPlot = ({
  datasets = DEFAULT_ARRAY,
  stacked = false,
  title,
  showTitle = true,
  loading,
  noTotal = false,
}) => {
  const [activeValues, setActiveValues] = useState([]);
  const [mouseInside, setMouseInside] = useState(false);

  const active = useMemo(() => {
    let defaultVal = noTotal ? datasets.map((val) => val.data.length - 1) : [];
    return mouseInside ? activeValues : defaultVal;
  }, [activeValues, noTotal, datasets, mouseInside]);

  const lineAtIndex = useMemo(() => {
    return mouseInside || noTotal ? active[0] : undefined;
  }, [mouseInside, noTotal, active]);

  const onHover = useCallback(
    (event, active) => {
      let newValues = active.map((obj) => obj.index);
      if (!PerformanceUtils.isEqual(newValues, activeValues)) {
        setActiveValues(newValues);
      }
    },
    [activeValues],
  );

  const { values, difference, dates } = useMemo(() => {
    let values = active.map((value, index) => datasets[index].data[value]?.y);
    if (values.length === 0 && datasets.length > 0) {
      values = datasets.map(({ data = [] }) =>
        data.reduce((total, data) => total + data?.y || 0, 0),
      );
    }

    const difference =
      values.length > 1 ? ((values[0] / values[1] - 1) * 100.0).toFixed(2) : undefined;

    const append = datasets[0]?.append;

    return {
      values: values.map((val) => (append ? `${val.toFixed(2)}${append}` : formatter.format(val))),
      difference,
      dates: values.map((val, index) => {
        let result = [datasets[index].data[active[index] || 0]?.text];
        if (active.length === 0 || (!mouseInside && !noTotal)) {
          result.push(datasets[index].data[datasets[index].data.length - 1]?.text);
        }
        return result;
      }),
    };
  }, [datasets, activeValues, mouseInside, active, noTotal]);

  return (
    <div className="fancy-plot">
      <div className="values">
        <div className="top">
          {showTitle && <span className="title">{title}</span>}
          {difference !== undefined && (
            <span className={`difference ${difference >= 0 ? 'green' : 'red'}`}>{difference}%</span>
          )}
        </div>
        <div className="details">
          {values.map((value, index) => (
            <div className={'detail i-' + index} key={index}>
              <span className="amount">{value}</span>
              <span className="date">
                {dates?.[index].length > 1
                  ? `${dates[index]?.[0]} to ${dates[index]?.[1]}`
                  : dates[index]?.[0]}
              </span>
            </div>
          ))}
        </div>
      </div>
      <div onMouseLeave={() => setMouseInside(false)} onMouseEnter={() => setMouseInside(true)}>
        <APlot
          height="200px"
          datasets={datasets}
          stacked={stacked}
          loading={loading}
          hideTitle={true}
          hideLegend={true}
          xScale={{
            type: 'linear',
            ticks: { display: false },
            max: (datasets?.[0]?.data?.length || 1) - 1,
          }}
          onHover={onHover}
          lineAtIndex={lineAtIndex}
        />
      </div>
    </div>
  );
};

export const APlot = ({
  datasets,
  height = '350px',
  forceHeight = false,
  stacked = false,
  title,
  hideTitle = false,
  hideLegend = false,
  loading,
  xScale,
  onHover,
  lineAtIndex,
}) => {
  const options = {
    onHover,
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    plugins: {
      colors: {
        enabled: true,
      },
      title: {
        display: !hideTitle,
        text: title || datasets[0]?.label,
      },
      legend: {
        display: !hideLegend && datasets.length > 1,
      },
      vline: {
        enabled: lineAtIndex !== undefined,
        index: lineAtIndex,
      },
      tooltip: lineAtIndex === undefined && {
        callbacks: {
          title: (items) => {
            if (items[0]?.raw?.text) {
              return items.map((item) => item.raw.text).join(' / ');
            }
            let value = items[0]?.raw?.x;
            if (value) {
              if (value instanceof moment) {
                return value.format('MMM YYYY');
              }
              if (typeof value === 'number') {
                return moment(value).format('MMM YYYY');
              }
              if (typeof value === 'string') {
                return moment(value, 'YYYY-MM').format('MMM YYYY');
              }
            }
          },
          label: (item) => {
            let value = item.parsed.y;
            return datasets[0]?.format
              ? formatter.format(value)
              : value + (datasets[0]?.append || '');
          },
        },
      },
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false,
    },
    layout: {
      padding: {
        right: 5,
      },
    },
    scales: {
      x: xScale || {
        type: 'time',
        time: {
          unit: 'month',
        },
      },
      y: {
        beginAtZero: true,
        stacked,
        ticks: {
          callback: (value) =>
            datasets[0]?.format ? formatter.format(value) : value + (datasets[0]?.append || ''),
        },
      },
    },
  };

  const data = {
    datasets: datasets.map((d) => {
      return {
        label: d.label,
        data: d.data,
        fill: true,
      };
    }),
  };

  return (
    <div className="chart" style={{ minHeight: height, height: forceHeight ? height : undefined }}>
      {loading && <div className="loading-indicator">Loading...</div>}
      <Line data={data} options={options} />
    </div>
  );
};
